import React, { useState, useEffect } from 'react';
import { Mail, Award, BookOpen, Scale, Loader2, Phone, MapPin, Star, Shield, Users } from 'lucide-react';
import LLSEOMeta from '../LLSEOMeta';

// Custom LinkedIn Icon as SVG
const LinkedInIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    className="w-5 h-5"
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const ImageWithLoading = ({ src, alt, className }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      )}
      <img 
        src={src} 
        alt={alt}
        className={className}
        loading="lazy"
        onLoad={() => setIsLoading(false)}
        onError={(e) => {
          setIsLoading(false);
          setError(true);
          e.target.src = '/assets/placeholder-attorney.webp';
        }}
        style={{ opacity: isLoading ? 0 : 1 }}
      />
    </div>
  );
};

const LLAttorneysPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [pageLoaded, setPageLoaded] = useState(false);

  useEffect(() => {
    setPageLoaded(true);
  }, []);

  const attorneys = [
    {
      id: 1,
      name: "Adv. Anandu A B",
      image: "/assets/Anandu_A_B.webp",
      position: "Senior Partner",
      mailId: "youngadv1997@gmail.com",
      linkedinUrl: "https://www.linkedin.com/in/anandu-a-b-1a07661b9",
      category: "partner",
      expertise: ["Criminal Law", "Cyber Law", "Civil Law", "Consumer Law", "Banking Law", 
      "Property Law", "Family Law", "Claim Cases", "Cheque Bounce Cases"],
      education: [
        "BA LL.B., Sree Narayana Guru College of Legal Studies, Kollam"
      ],
      barAdmissions: [
        "Bar Council of Kerala",
        "Kerala District Court, Kollam"
      ],
      experience: "5+ years",
      courtAppearances: "Kollam District Court, Kerala High Court",
      successRate: "92%",
      casesHandled: "500+",
      bio: "Anandu is a seasoned lawyer with 5 years of experience in Kollam District Court delivering tailored legal solutions. Specializes in criminal defense, cyber law, and civil litigation with a proven track record in Kerala courts. Successfully argued several landmark cases in Kollam and Kerala High Court.",
    },
    {
      id: 2,
      name: "Adv. Sreeshma Suresh",
      image: "/assets/Sreeshma_Suresh.webp",
      mailId: "sreeshmassuresh27@gmail.com",
      linkedinUrl: "https://www.linkedin.com/in/adv-sreeshma-suresh-5238bb229",
      position: "Managing Partner",
      category: "partner",
      expertise: ["Civil Law", "Registration Law", "Family Law", "Property Disputes", "Cheque Bounce Cases"],
      education: [
        "BA LL.B., Sree Narayana Guru College of Legal Studies, Kollam"
      ],
      barAdmissions: [
        "Bar Council of Kerala",
        "Kollam District Court"
      ],
      experience: "5+ years",
      courtAppearances: "Kollam District Court, Family Courts Kerala",
      successRate: "95%",
      casesHandled: "300+",
      bio: "Sreeshma specializes in civil law, family law, and property disputes in Kollam. Known for her compassionate and thorough approach to family matters and property registration cases. Expert in handling divorce, custody, and maintenance cases across Kerala family courts."
    },
    {
      id: 3,
      name: "Adv. Arjun A B",
      image: "/assets/Arjun_A_B.webp",
      mailId: "abarjun854@gmail.com",
      linkedinUrl: "https://www.linkedin.com/in/arjun-ab-224048329",
      position: "Associate Attorney",
      category: "associate",
      expertise: ["Criminal Law", "Civil Law", "Cyber Law", "Bail Applications", "Cheque Bounce Cases"],
      education: [
        "BA LL.B., Sree Narayana Guru College of Legal Studies, Kollam"
      ],
      barAdmissions: [
        "Bar Council of Kerala",
        "Kerala District Court, Kollam"
      ],
      experience: "2+ years",
      courtAppearances: "Kollam District Court, Magistrate Courts",
      successRate: "88%",
      casesHandled: "200+",
      bio: "Arjun is an emerging legal professional in Kollam specializing in criminal defense and civil law matters. Expert in bail applications, anticipatory bail, and criminal trial defense across Kollam courts. Known for swift legal action in emergency cases."
    }
  ];

  // Schema markup for each attorney
  useEffect(() => {
    const attorneySchemas = attorneys.map(attorney => ({
      "@context": "https://schema.org",
      "@type": "Attorney",
      "name": attorney.name,
      "description": attorney.bio,
      "image": `https://loopholelaw.in${attorney.image}`,
      "email": attorney.mailId,
      "url": attorney.linkedinUrl,
      "jobTitle": attorney.position,
      "knowsAbout": attorney.expertise,
      "alumniOf": {
        "@type": "EducationalOrganization",
        "name": "Sree Narayana Guru College of Legal Studies",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Kollam",
          "addressRegion": "Kerala",
          "addressCountry": "IN"
        }
      },
      "memberOf": {
        "@type": "Organization",
        "name": "LoopHoleLaw",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1st Floor, Ganga Building, Opposite Kollam Collectorate",
          "addressLocality": "Kollam",
          "addressRegion": "Kerala",
          "postalCode": "691013",
          "addressCountry": "IN"
        }
      },
      "workLocation": {
        "@type": "Place",
        "name": "Kollam District Court",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Kollam",
          "addressRegion": "Kerala",
          "addressCountry": "IN"
        }
      },
      "knowsLanguage": ["English", "Malayalam", "Hindi"]
    }));

    // Organization schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "LegalService",
      "name": "LoopHoleLaw Legal Team - Best Lawyers in Kollam",
      "description": "Experienced legal team in Kollam, Kerala specializing in Criminal, Civil, Family, Cyber, and Property Law. 7+ years combined experience in Kollam District Court and Kerala High Court.",
      "url": "https://loopholelaw.in/team/attorneys",
      "telephone": "+919539650421",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "1st Floor, Ganga Building, Opposite Kollam Collectorate",
        "addressLocality": "Kollam",
        "addressRegion": "Kerala",
        "postalCode": "691013",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "8.892817793613391",
        "longitude": "76.57290401478547"
      },
      "employee": attorneySchemas,
      "areaServed": {
        "@type": "City",
        "name": "Kollam"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [organizationSchema, ...attorneySchemas]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const categories = [
    { id: 'all', label: 'All Attorneys' },
    { id: 'partner', label: 'Partners' },
    { id: 'associate', label: 'Associates' }
  ];

  const filteredAttorneys = activeTab === 'all' 
    ? attorneys 
    : attorneys.filter(attorney => attorney.category === activeTab);

  const stats = [
    { value: "7+", label: "Years Combined Experience" },
    { value: "100+", label: "Cases Successfully Won" },
    { value: "92%", label: "Average Success Rate" },
    { value: "3", label: "Expert Attorneys in Kollam" }
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <LLSEOMeta 
        title="Best Lawyers in Kollam | Expert Legal Team Kerala | Criminal, Civil & Family Law | LoopHoleLaw"
        description="Meet LoopHoleLaw's experienced lawyers in Kollam, Kerala. Expert attorneys specializing in Criminal Law, Civil Litigation, Cyber Law, Family Law & Property disputes. 7+ years experience in Kollam District Court. കൊല്ലത്തെ അനുഭവസമ്പന്നരായ അഭിഭാഷകർ. Call: +91 9539650421"
        keywords="lawyers in Kollam, attorneys Kollam Kerala, criminal lawyers Kollam, civil lawyers Kerala, family law attorneys Kollam, property lawyers Kollam, cyber law experts Kerala, best advocates Kollam, Kollam district court lawyers, legal team Kerala, കൊല്ലം അഭിഭാഷകർ, LoopHoleLaw team"
        canonicalPath="/team"
      />
      
      <div className="bg-gradient-to-b from-slate-900 to-black">
        <div className="min-h-screen pt-24 pb-12">
          <div className="max-w-7xl mx-auto px-4">

            {/* Header Section */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-green-500/10 rounded-full border border-green-500/30">
                <MapPin className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm">Serving Kollam District, Kerala</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
                Expert Lawyers in Kollam, Kerala
              </h1>
              <p className="text-2xl md:text-3xl text-blue-400 mb-6">
                കൊല്ലത്തെ അനുഭവസമ്പന്നരായ അഭിഭാഷകർ
              </p>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                Meet our experienced legal team with 7+ years combined experience in Kollam District Court 
                and Kerala High Court. Specialized in Criminal, Civil, Cyber, Family & Property Law.
              </p>

              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center gap-6 mb-8">
                <div className="flex items-center gap-2 text-gray-300">
                  <Shield className="w-5 h-5 text-green-400" />
                  <span>Bar Council of Kerala Certified</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Award className="w-5 h-5 text-green-400" />
                  <span>Kollam District Court Experts</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Users className="w-5 h-5 text-green-400" />
                  <span>English, മലയാളം, हिन्दी</span>
                </div>
              </div>
            </div>

            {/* Stats Section */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              {stats.map((stat, index) => (
                <div 
                  key={index}
                  className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center hover:border-green-400/50 transition-all"
                >
                  <div className="text-3xl md:text-4xl font-bold text-green-400 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Expertise Overview */}
            <div className="bg-gradient-to-r from-blue-500/10 to-green-500/10 border border-slate-700 rounded-xl p-8 mb-16">
              <h2 className="text-2xl font-bold text-white text-center mb-6">
                Comprehensive Legal Expertise in Kollam
              </h2>
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  "Criminal Defense in Kollam",
                  "Civil Litigation Kerala",
                  "Cyber Crime Law",
                  "Family Law Kollam",
                  "Property Disputes",
                  "Consumer Protection",
                  "Banking Law",
                  "Cheque Bounce Cases",
                  "Bail Applications",
                  "Registration Law"
                ].map((area) => (
                  <span
                    key={area}
                    className="px-4 py-2 bg-slate-800/50 text-gray-300 rounded-full text-sm border border-slate-700 hover:border-green-400/50 transition-all"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex justify-center mb-12">
              <div className="inline-flex bg-slate-800/50 rounded-lg p-1 border border-slate-700">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setActiveTab(category.id)}
                    className={`px-6 py-3 rounded-lg transition-all font-medium ${
                      activeTab === category.id 
                        ? 'bg-green-500 text-white shadow-lg' 
                        : 'text-gray-400 hover:text-white hover:bg-slate-700/50'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Attorneys Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {filteredAttorneys.map((attorney, index) => (
                <article 
                  key={attorney.id}
                  className="bg-slate-800/50 rounded-xl overflow-hidden border border-slate-700 group hover:border-green-500/50 transition-all hover:shadow-xl hover:shadow-green-500/10"
                  itemScope 
                  itemType="https://schema.org/Attorney"
                >
                  {/* Attorney Image */}
                  <div className="aspect-square relative overflow-hidden">
                    <ImageWithLoading 
                      src={attorney.image} 
                      alt={`${attorney.name} - ${attorney.position} at LoopHoleLaw Kollam`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-1" itemProp="name">
                        {attorney.name}
                      </h3>
                      <p className="text-green-400 font-medium" itemProp="jobTitle">
                        {attorney.position}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-300">
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          {attorney.successRate}
                        </span>
                        <span>{attorney.experience}</span>
                      </div>
                    </div>
                  </div>

                  {/* Attorney Details */}
                  <div className="p-6 space-y-4">

                    {/* Court Appearances */}
                    <div>
                      <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                        <Scale className="w-4 h-4 text-green-400" />
                        Court Experience
                      </h4>
                      <p className="text-gray-400 text-sm">{attorney.courtAppearances}</p>
                    </div>

                    {/* Expertise */}
                    <div>
                      <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                        <Award className="w-4 h-4 text-green-400" />
                        Areas of Practice
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {attorney.expertise.slice(0, 4).map((area, idx) => (
                          <span 
                            key={idx}
                            className="px-3 py-1 bg-slate-700/50 rounded-full text-xs text-gray-300 border border-slate-600"
                            itemProp="knowsAbout"
                          >
                            {area}
                          </span>
                        ))}
                        {attorney.expertise.length > 4 && (
                          <span className="px-3 py-1 bg-green-500/10 rounded-full text-xs text-green-400 border border-green-500/30">
                            +{attorney.expertise.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Education */}
                    <div>
                      <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-green-400" />
                        Education & Bar
                      </h4>
                      <ul className="text-gray-400 text-sm space-y-1">
                        {attorney.education.map((edu, idx) => (
                          <li key={idx} itemProp="alumniOf">{edu}</li>
                        ))}
                        {attorney.barAdmissions.map((bar, idx) => (
                          <li key={idx} className="text-green-400">✓ {bar}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Bio */}
                    <p className="text-gray-300 text-sm leading-relaxed" itemProp="description">
                      {attorney.bio}
                    </p>

                    {/* Contact */}
                    <div className="flex gap-3 pt-4 border-t border-slate-700">
                      <a 
                        href={`mailto:${attorney.mailId}`}
                        className="flex-1 flex items-center justify-center gap-2 p-3 rounded-lg bg-slate-700 hover:bg-green-500 text-white transition-all"
                        itemProp="email"
                        aria-label={`Email ${attorney.name}`}
                      >
                        <Mail className="w-5 h-5" />
                        <span className="text-sm font-medium">Email</span>
                      </a>
                      <a 
                        href={attorney.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 p-3 rounded-lg bg-slate-700 hover:bg-blue-600 text-white transition-all"
                        itemProp="url"
                        aria-label={`View ${attorney.name} on LinkedIn`}
                      >
                        <LinkedInIcon />
                        <span className="text-sm font-medium">LinkedIn</span>
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Why Choose Our Team */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-16">
              <h2 className="text-3xl font-bold text-white text-center mb-8">
                Why Choose Our Legal Team in Kollam?
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="bg-blue-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Local Court Expertise</h3>
                  <p className="text-gray-400 text-sm">
                    Deep knowledge of Kollam District Court procedures and Kerala court systems. 
                    7+ years combined experience.
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-green-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Proven Success Rate</h3>
                  <p className="text-gray-400 text-sm">
                    Average 92% success rate across 100+ cases in Criminal, Civil, and Family law matters.
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-blue-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Multilingual Service</h3>
                  <p className="text-gray-400 text-sm">
                    Fluent in English, Malayalam (മലയാളം), and Hindi. Comfortable communication in your language.
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-16">
              <h2 className="text-2xl font-bold text-white mb-8">
                Frequently Asked Questions About Our Lawyers
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Are your lawyers registered with the Bar Council of Kerala?
                  </h3>
                  <p className="text-gray-400">
                    Yes, all our attorneys are fully registered and certified by the Bar Council of Kerala 
                    and authorized to practice in Kollam District Court and Kerala High Court.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Do you handle cases outside Kollam?
                  </h3>
                  <p className="text-gray-400">
                    While our primary practice is in Kollam District Court, we also handle cases across 
                    Kerala including appearances in Kerala High Court, Ernakulam.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Can I meet the attorney before hiring?
                  </h3>
                  <p className="text-gray-400">
                    Absolutely! We offer free initial consultations where you can meet our attorneys, 
                    discuss your case, and understand our approach before making any commitment.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    What languages do your attorneys speak?
                  </h3>
                  <p className="text-gray-400">
                    Our legal team is fluent in English, Malayalam (മലയാളം), and Hindi, ensuring 
                    comfortable communication for all clients across Kerala.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact CTA */}
            <div className="text-center bg-gradient-to-r from-blue-500/10 to-green-500/10 rounded-xl p-12 border border-slate-700">
              <h2 className="text-3xl font-bold text-white mb-4">
                Need Expert Legal Representation in Kollam?
              </h2>
              <p className="text-xl text-gray-300 mb-2">
                കൊല്ലത്ത് വിദഗ്ധ നിയമ സഹായം ആവശ്യമുണ്ടോ?
              </p>
              <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
                Our experienced team of attorneys is ready to help you with criminal defense, 
                civil litigation, family law, property disputes, and cyber crime cases.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-all text-lg shadow-lg hover:shadow-green-500/50"
                >
                  <Phone className="w-5 h-5" />
                  Schedule Free Consultation
                </a>
                <a 
                  href="tel:+919539650421"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white hover:bg-white/10 text-white font-semibold rounded-lg transition-all text-lg"
                >
                  <Phone className="w-5 h-5" />
                  Call +91 9539650421
                </a>
              </div>
              <p className="text-gray-500 mt-6 text-sm">
                Office: 1st Floor, Ganga Building, Opposite Kollam Collectorate, Kollam - 691013
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LLAttorneysPage;